import { useState, useEffect } from "react";
import "./Calendar/Calendar.css";
import CalendarHeader from "./Calendar/CalendarHeader";
import MonthView from "./Calendar/MonthView";
import YearView from "./Calendar/YearView";
import StatusBadge from "./Calendar/StatusBadge";
import { MONTH_FULL } from "../../../constants";
import EditAttendance from "./EditAttendance";
import SubmitBanner from "./SubmitBanner";
import axios from "../../../axios";
import { getWeekDays } from "../../../utils";

export default function AttendanceCalendar({ tuition, onBack }) {
    const [view, setView] = useState("week"); // "week" | "month" | "year"
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [calMonth, setCalMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
    const [calYear, setCalYear] = useState(new Date().getFullYear());
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [isVisible, setIsVisible] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [showFullMonthClasses, setShowFullMonthClasses] = useState(false);

    const [attendanceRecords, setAttendanceRecords] = useState({});
    const [loading, setLoading] = useState(false);

    // Responsive listener
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener("resize", handler);

        // Fetch submit banner status on mount
        axios.get(`/v1/tutor/submit-banner-status?dealId=${tuition?.deal_id}`)
            .then(res => {
                if (res.data?.is_success) {
                    setIsVisible(res.data.data.showBanner);
                }
            })
            .catch(err => console.error("Error fetching banner status:", err));

        return () => window.removeEventListener("resize", handler);
    }, []);

    const fetchAttendanceHistory = async () => {
        if (!tuition?.student_id) return;
        try {
            setLoading(true);
            const pad = (n) => String(n).padStart(2, "0");
            const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

            let startDate, endDate;
            if (view === "week") {
                const days = getWeekDays(selectedDate);
                startDate = fmt(days[0]);
                endDate = fmt(days[days.length - 1]);
            } else {
                const firstDay = new Date(calMonth.year, calMonth.month, 1);
                const lastDay = new Date(calMonth.year, calMonth.month + 1, 0);
                startDate = fmt(firstDay);
                endDate = fmt(lastDay);
            }

            const response = await axios.get(`/v1/attendance/history?startDate=${startDate}&endDate=${endDate}`);
            if (response.data.is_success) {
                const records = response.data.data;
                const recordMap = {};

                records.forEach(rec => {
                    if (rec.student_id == tuition.student_id) {
                        recordMap[rec.date] = {
                            status: rec.status ? "completed" : "cancelled",
                            reason: rec.reason,
                            updated_at: rec.updated_at || rec.updatedAt || null,
                        };
                    }
                });
                setAttendanceRecords(prev => ({ ...prev, ...recordMap }));
            }
        } catch (error) {
            console.error("Error fetching attendance history", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAttendanceHistory();
    }, [calMonth, calYear, tuition?.student_id, selectedDate, view]);

    useEffect(() => {
        if (view === "week") {
            const y = selectedDate.getFullYear();
            const m = selectedDate.getMonth();
            if (y !== calMonth.year || m !== calMonth.month) {
                setCalMonth({ year: y, month: m });
            }
        }
    }, [selectedDate, view]);

    const headerTitle = () => {
        if (view === "week") {
            return selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
        }
        if (view === "month") return `${MONTH_FULL[calMonth.month]} ${calMonth.year}`;
        return '';
    };

    const handleYearMonthSelect = (mi) => {
        setCalMonth({ year: calYear, month: mi });
        setView("month");
    };

    const prevNav = () => {
        if (view === "month") {
            let m = calMonth.month - 1, y = calMonth.year;
            if (m < 0) { m = 11; y--; }
            setCalMonth({ year: y, month: m });
        } else if (view === "year") {
            setCalYear(y => y - 1);
        }
    };

    const nextNav = () => {
        if (view === "month") {
            let m = calMonth.month + 1, y = calMonth.year;
            if (m > 11) { m = 0; y++; }
            setCalMonth({ year: y, month: m });
        } else if (view === "year") {
            setCalYear(y => y + 1);
        }
    };

    const isFutureDate = (dateObj) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(dateObj);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate > today;
    };

    const getDatesInMonth = (year, month) => {
        const dates = [];
        const numDays = new Date(year, month + 1, 0).getDate();
        for (let i = 1; i <= numDays; i++) {
            dates.push(new Date(year, month, i));
        }
        return dates;
    };

    const formatDateStr = (dateObj) => {
        // Formatter matching the backend date format YYYY-MM-DD
        const offset = dateObj.getTimezoneOffset()
        dateObj = new Date(dateObj.getTime() - (offset * 60 * 1000))
        return dateObj.toISOString().split('T')[0]
    };

    const getClassesThisMonth = () => {
        let classes = getDatesInMonth(calMonth.year, calMonth.month).map(dateObj => {
            const dateStr = formatDateStr(dateObj);
            const record = attendanceRecords[dateStr];

            // Format updated_at into a human-readable local time (e.g. "3:45 pm")
            let timeLabel = null;
            const rawTime = record?.updated_at;
            if (rawTime) {
                // MySQL date strings (YYYY-MM-DD HH:MM:SS) need 'T' for cross-browser reliability (Safari)
                const dateStrForParse = typeof rawTime === 'string' ? rawTime.replace(' ', 'T') : rawTime;
                const d = new Date(dateStrForParse);
                if (!isNaN(d.getTime())) {
                    timeLabel = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
                }
            }

            return {
                rawDate: dateObj,
                dateStr: dateStr,
                date: dateObj.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric" }),
                time: timeLabel,
                status: record?.status,
                reason: record?.reason,
                isFuture: isFutureDate(dateObj)
            };
        });

        classes.sort((a, b) => {
            if (a.rawDate.toDateString() === selectedDate.toDateString()) return -1;
            if (b.rawDate.toDateString() === selectedDate.toDateString()) return 1;
            return b.rawDate - a.rawDate;
        });

        return classes;
    };

    const classesThisMonth = getClassesThisMonth();

    // For week view past classes (meaning < selectedDate within the month)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pastClasses = classesThisMonth.filter(c => c.rawDate.getTime() < selectedDate.getTime() && !c.isFuture && !!c.status && c.rawDate.getTime() !== today.getTime());
    const selectedClassObj = classesThisMonth.find(c => c.rawDate.toDateString() === selectedDate.toDateString());

    const monthViewCompletedCount = classesThisMonth.filter(c => c.status === "completed").length;

    const openEditModal = (cls) => {
        if (cls.isFuture) return;
        setSelectedClass(cls);
        setIsModalOpen(true);
    };

    const closeEditModal = () => {
        setIsModalOpen(false);
        setSelectedClass(null);
    };

    const handleFormSubmit = async (formData) => {
        try {
            await axios.post("/v1/attendance/mark", {
                studentId: tuition.student_id,
                date: selectedClass.dateStr,
                status: formData.isConducted,
                reason: formData.reason
            });
            await fetchAttendanceHistory();
            // setIsVisible(true);
            closeEditModal();
        } catch (err) {
            console.error(err);
        }
    };

    const handleBannerSubmit = async () => {
        try {
            await axios.post("/v1/tutor/dismiss-submit-banner", { dealId: tuition?.deal_id });
            setIsVisible(false);
        } catch (err) {
            console.error("Error dismissing banner:", err);
        }
    };

    const renderCard = (cls) => {
        if (!cls) return null;
        const hasRecord = !!cls.status;
        return (
            <div className="attendance-card" key={cls.dateStr}>
                <div className="card-header">
                    <div className="weekly-card-details-wrapper">
                        <div className="card-date">{cls.date}</div>
                        {hasRecord && <div className="card-time"><img src="clock.svg" alt="clock" /> {cls.time}</div>}
                    </div>
                    {hasRecord && <StatusBadge status={cls.status} />}
                </div>
                {!cls.isFuture && (
                    <button className={`edit-btn ${!hasRecord ? "add-btn" : ""}`} onClick={() => openEditModal({
                        title: `${tuition?.student_name} - ${tuition?.subject} tuition`,
                        date: cls.date,
                        dateStr: cls.dateStr,
                        time: cls.time,
                        conducted: cls.status === "completed",
                        reason: cls.reason,
                        isAdd: !hasRecord
                    })}>
                        {!hasRecord ? "Add Attendance" : "Edit Attendance"} {!hasRecord ? <span style={{ marginLeft: "4px" }}>+</span> : <img src="pencil.svg" alt="edit" />}
                    </button>
                )}
            </div>
        );
    };

    const isToday = selectedClassObj?.rawDate.toDateString() === new Date().toDateString();

    return (
        <div className="calendar-container">
            <CalendarHeader
                view={view}
                setView={setView}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                calMonth={calMonth}
                calYear={calYear}
                isMobile={isMobile}
                prevNav={prevNav}
                nextNav={nextNav}
                headerTitle={headerTitle}
                tuition={tuition}
                onBack={onBack}
            />

            <div className={`calendar-body ${isMobile ? 'mobile' : ''}`}>
                {/* WEEK VIEW body */}
                {view === "week" && (
                    <div className="week-classes-container">
                        <div className="attendance-classes-title">{isToday ? "Today" : "Selected Date"}</div>
                        {renderCard(selectedClassObj)}
                        <div className="horizontal-devider" />
                        <div className="past-classes-container">
                            <div className="attendance-classes-title">Past classes</div>
                            <div className="past-classes">
                                {pastClasses.length > 0 ? Object.values(pastClasses).map(cls => renderCard(cls)) : <div style={{ color: "#666", padding: "10px" }}>No past classes found in this month.</div>}
                            </div>
                        </div>
                    </div>
                )}

                {/* MONTH VIEW body */}
                {view === "month" && (
                    <div style={{ display: isMobile ? "flex" : "grid", flexDirection: isMobile ? "column" : "", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
                        <div>
                            <div className="month-view-controls">
                                <div className="month-label">{MONTH_FULL[calMonth.month]} {calMonth.year}</div>
                                <div className="month-nav-btns">
                                    <button onClick={prevNav} className="nav-btn nav-btn-secondary">‹</button>
                                    <button onClick={nextNav} className="nav-btn nav-btn-secondary">›</button>
                                </div>
                            </div>
                            <MonthView
                                year={calMonth.year}
                                month={calMonth.month}
                                selectedDate={selectedDate}
                                onSelectDate={setSelectedDate}
                                isMobile={isMobile}
                                attendanceData={attendanceRecords}
                            />
                        </div>

                        <div className="classes-wrapper">
                            <div className="section-title-dark" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                Classes this month ({monthViewCompletedCount} completed)
                                {isMobile && (
                                    <span 
                                        className="view-more-link" 
                                        onClick={() => setShowFullMonthClasses(true)}
                                    >
                                        View More
                                    </span>
                                )}
                            </div>
                            <div className="classes-card-wrapper">
                                {(() => {
                                    const monthClasses = classesThisMonth.filter(cls => {
                                        if (cls.isFuture) return false;
                                        return !!cls.status;
                                    });
                                    if (monthClasses.length === 0) {
                                        return <div style={{ color: "#666", padding: "10px" }}>No classes found in this month.</div>;
                                    }
                                    return monthClasses.map((cls) => renderCard(cls));
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* YEAR VIEW body */}
                {view === "year" && (
                    <div>
                        <YearView year={calYear} onSelectMonth={handleYearMonthSelect} isMobile={isMobile} />
                    </div>
                )}
            </div>
            <EditAttendance
                isOpen={isModalOpen}
                onClose={closeEditModal}
                data={selectedClass}
                onSubmit={handleFormSubmit}
            />
            {isVisible && (
                <SubmitBanner
                    onSubmit={handleBannerSubmit}
                    onClose={() => setIsVisible(false)}
                />
            )}

            {isMobile && showFullMonthClasses && (
                <div className="full-month-classes-view">
                    <div className="full-view-header">
                        <button className="header-back-btn" onClick={() => setShowFullMonthClasses(false)}>←</button>
                        <span className="header-page-title">Attendance</span>
                    </div>
                    <div className="full-view-content">
                        <div className="attendance-classes-title">Classes this month</div>
                        <div className="classes-card-wrapper">
                            {classesThisMonth
                                .filter(cls => !cls.isFuture && !!cls.status)
                                .map((cls) => renderCard(cls))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
